import Papa from 'papaparse';
import { z } from 'zod';

export const athleteSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  sport: z.string().min(1, "Sport is required"),
  year: z.enum(["freshman", "sophomore", "junior", "senior", "graduate"], {
    errorMap: () => ({ message: "Year must be: freshman, sophomore, junior, senior, or graduate" })
  }),
  gender: z.enum(["male", "female", "other"], {
    errorMap: () => ({ message: "Gender must be: male, female, or other" })
  }),
});

export type AthleteRow = z.infer<typeof athleteSchema>;

export interface ValidationResult {
  row: number;
  data: AthleteRow;
  status: 'success' | 'warning' | 'error';
  errors: string[];
}

export interface ParsedResult {
  valid: ValidationResult[];
  invalid: ValidationResult[];
  summary: {
    total: number;
    valid: number;
    invalid: number;
  };
}

const normalizeYear = (year: string): string => {
  const normalized = year.toLowerCase().trim();
  const yearMap: Record<string, string> = {
    '1st year': 'freshman',
    'first year': 'freshman',
    'fr': 'freshman',
    '2nd year': 'sophomore',
    'second year': 'sophomore',
    'so': 'sophomore',
    'soph': 'sophomore',
    '3rd year': 'junior',
    'third year': 'junior',
    'jr': 'junior',
    '4th year': 'senior',
    'fourth year': 'senior',
    'sr': 'senior',
    'grad': 'graduate',
    'fifth year': 'graduate',
    '5th year': 'graduate',
  };
  return yearMap[normalized] || normalized;
};

export const parseCSV = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.toLowerCase().trim(),
      complete: (results) => {
        resolve(results.data);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

export const validateAthletes = (
  data: any[],
  existingEmails: Set<string>
): ParsedResult => {
  const valid: ValidationResult[] = [];
  const invalid: ValidationResult[] = [];
  const emailsInFile = new Set<string>();

  data.forEach((row, index) => {
    const errors: string[] = [];
    const rowNumber = index + 2; // +2 for header row and 0-indexing

    // Normalize year value
    if (row.year) {
      row.year = normalizeYear(row.year);
    }

    // Normalize gender value
    if (row.gender) {
      row.gender = row.gender.toLowerCase().trim();
    }

    // Check for duplicate emails within file
    if (row.email) {
      const email = row.email.toLowerCase().trim();
      if (emailsInFile.has(email)) {
        errors.push(`Duplicate email in file: ${email}`);
      } else {
        emailsInFile.add(email);
      }

      // Check for existing emails in database
      if (existingEmails.has(email)) {
        errors.push(`Email already exists in database: ${email}`);
      }
    }

    // Validate against schema
    const result = athleteSchema.safeParse(row);
    
    if (result.success) {
      if (errors.length > 0) {
        invalid.push({
          row: rowNumber,
          data: row,
          status: 'error',
          errors,
        });
      } else {
        valid.push({
          row: rowNumber,
          data: result.data,
          status: 'success',
          errors: [],
        });
      }
    } else {
      const zodErrors = result.error.errors.map(err => 
        `${err.path.join('.')}: ${err.message}`
      );
      invalid.push({
        row: rowNumber,
        data: row,
        status: 'error',
        errors: [...errors, ...zodErrors],
      });
    }
  });

  return {
    valid,
    invalid,
    summary: {
      total: data.length,
      valid: valid.length,
      invalid: invalid.length,
    },
  };
};

export const generateCSVTemplate = (): string => {
  const headers = ['name', 'email', 'phone', 'sport', 'year', 'gender'];
  const example = [
    'John Smith',
    'john.smith@university.edu',
    '555-0123',
    'Football',
    'sophomore',
    'male',
  ];
  
  return `${headers.join(',')}\n${example.join(',')}`;
};

export const downloadTemplate = () => {
  const csv = generateCSVTemplate();
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'athletes-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const downloadErrorReport = (invalid: ValidationResult[]) => {
  const headers = ['Row', 'Data', 'Errors'];
  const rows = invalid.map(result => [
    result.row.toString(),
    JSON.stringify(result.data),
    result.errors.join('; '),
  ]);
  
  const csv = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');
  
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'import-errors.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
